import React from 'react';
import { Link } from 'react-router-dom';
import './TermsOfService.css';

const TermsOfService = () => {
  return (
    <div className="terms-of-service">
      {/* Header */}
      <header className="terms-header">
        <div className="container">
          <Link to="/" className="back-button">← Back to Home</Link>
          <h1>Terms of Service</h1>
          <p className="last-updated">Last Updated: December 28, 2025</p>
        </div>
      </header>

      {/* Content */}
      <div className="terms-content">
        <div className="container">
          <section className="terms-section">
            <h2>Agreement to Terms</h2>
            <p>
              Welcome to RunningAI Pro! These Terms of Service ("Terms") govern your access to and use of the RunningAI Pro mobile
              application ("App", "Service", or "RunningAI Pro"). By downloading, installing, or using RunningAI Pro, you agree to
              be bound by these Terms. If you do not agree to these Terms, do not use the App.
            </p>
            <p>
              These Terms constitute a legally binding agreement between you ("User", "you", or "your") and RunningAI Pro
              ("we", "us", or "our"). We reserve the right to modify these Terms at any time. Your continued use of the
              App after changes are posted constitutes acceptance of the modified Terms.
            </p>
          </section>

          <section className="terms-section">
            <h2>Eligibility</h2>
            <p>You must meet the following requirements to use RunningAI Pro:</p>
            <ul>
              <li>Be at least 13 years of age (or the age of majority in your jurisdiction)</li>
              <li>Have the legal capacity to enter into a binding contract</li>
              <li>Not be prohibited from using the Service under applicable laws</li>
              <li>Provide accurate and complete registration information</li>
            </ul>
            <p>
              If you are under 18, you represent that you have your parent or guardian's permission to use the App and
              they have agreed to these Terms on your behalf.
            </p>
          </section>

          <section className="terms-section">
            <h2>Account Registration and Security</h2>

            <h3>Account Creation</h3>
            <p>To access certain features, you must create an account by providing:</p>
            <ul>
              <li>A valid email address</li>
              <li>A secure password</li>
              <li>Optional profile information</li>
            </ul>

            <h3>Account Responsibilities</h3>
            <p>You agree to:</p>
            <ul>
              <li>Provide accurate, current, and complete information</li>
              <li>Maintain the security of your password and account</li>
              <li>Notify us immediately of any unauthorized access</li>
              <li>Accept responsibility for all activities under your account</li>
              <li>Not share your account credentials with others</li>
              <li>Not create an account using a false identity or impersonate others</li>
            </ul>

            <h3>Account Termination</h3>
            <p>
              We reserve the right to suspend or terminate your account at any time if you violate these Terms or engage
              in activities that harm the Service or other users.
            </p>
          </section>

          <section className="terms-section">
            <h2>Subscriptions and Payments</h2>

            <h3>Premium Subscription</h3>
            <p>RunningAI Pro offers optional premium subscriptions ("Premium") with enhanced features including:</p>
            <ul>
              <li>Unlimited AI coach interactions</li>
              <li>Unlimited personalized training plans</li>
              <li>Race time predictions for all distances</li>
              <li>Advanced analytics and insights</li>
            </ul>

            <h3>Free Tier Limitations</h3>
            <p>Free users have access to core features with the following limitations:</p>
            <ul>
              <li>3 AI coach messages per month</li>
              <li>2 run analyses per month</li>
              <li>1 training plan generation per month</li>
            </ul>

            <h3>Billing and Payment</h3>
            <p>
              Subscriptions are billed through Apple's In-App Purchase system. By subscribing, you authorize Apple to
              charge your payment method on a recurring basis according to your chosen subscription plan (monthly or yearly).
            </p>
            <ul>
              <li><strong>Auto-Renewal:</strong> Subscriptions automatically renew unless canceled at least 24 hours before the end of the current period</li>
              <li><strong>Price Changes:</strong> We may change subscription prices with 30 days notice. Changes apply to future billing cycles</li>
              <li><strong>Payment Processing:</strong> All payments are processed by Apple. We do not store your payment information</li>
            </ul>

            <h3>Cancellation and Refunds</h3>
            <p>
              You may cancel your subscription at any time through your Apple account settings. Cancellation takes effect
              at the end of your current billing period. You will retain Premium access until that date.
            </p>
            <p>
              <strong>Refund Policy:</strong> All subscription purchases are subject to Apple's refund policy. We do not
              process refunds directly. To request a refund, contact Apple Support or visit reportaproblem.apple.com.
              Refunds are granted at Apple's sole discretion.
            </p>
            <p>
              <strong>No Partial Refunds:</strong> We do not provide partial refunds for unused portions of subscription periods.
            </p>
          </section>

          <section className="terms-section">
            <h2>Acceptable Use Policy</h2>
            <p>You agree NOT to use RunningAI Pro to:</p>

            <div className="prohibited-use">
              <h4>Prohibited Activities</h4>
              <ul>
                <li>Violate any local, state, national, or international laws</li>
                <li>Harass, abuse, threaten, or intimidate other users</li>
                <li>Post false, misleading, or fraudulent information</li>
                <li>Impersonate any person or entity</li>
                <li>Upload malicious code, viruses, or harmful software</li>
                <li>Attempt to gain unauthorized access to the Service or other accounts</li>
                <li>Scrape, crawl, or use automated tools to access the Service</li>
                <li>Reverse engineer, decompile, or disassemble the App</li>
                <li>Remove or modify any copyright, trademark, or proprietary notices</li>
                <li>Use the Service for commercial purposes without authorization</li>
                <li>Share inappropriate, offensive, or illegal content</li>
                <li>Spam or send unsolicited messages to other users</li>
              </ul>
            </div>

            <h3>Community Guidelines</h3>
            <p>When using community features, you must:</p>
            <ul>
              <li>Be respectful and supportive of other runners</li>
              <li>Share accurate information about your runs and achievements</li>
              <li>Not manipulate GPS data or falsify running metrics</li>
              <li>Report inappropriate behavior or content</li>
              <li>Respect the privacy of other users</li>
            </ul>
          </section>

          <section className="terms-section">
            <h2>User Content and Data</h2>

            <h3>Your Content</h3>
            <p>
              You retain ownership of all data you create or upload to RunningAI Pro, including running activities, profile
              information, and messages. By using the Service, you grant us a limited license to use, store, and process
              your data as necessary to provide the Service.
            </p>

            <h3>Community Features</h3>
            <p>
              When you share runs or achievements with the community, you grant other users the right to view that shared
              content. You can control what you share through privacy settings.
            </p>

            <h3>AI-Generated Content</h3>
            <p>
              Training plans, coaching advice, and race predictions generated by our AI coach are personalized to you.
              While we strive for accuracy, AI-generated content is for informational purposes only and should not replace
              professional medical or coaching advice.
            </p>

            <h3>Data Deletion</h3>
            <p>
              You may delete individual runs or your entire account at any time. Upon account deletion, your data will be
              permanently removed from our active servers within 30 days.
            </p>
          </section>

          <section className="terms-section">
            <h2>Intellectual Property Rights</h2>

            <h3>RunningAI Pro Property</h3>
            <p>
              The App, including its design, features, code, graphics, logos, and trademarks, is owned by RunningAI Pro and
              protected by copyright, trademark, and other intellectual property laws. You may not copy, modify, distribute,
              sell, or create derivative works from any part of the Service without our written permission.
            </p>

            <h3>License Grant</h3>
            <p>
              We grant you a limited, non-exclusive, non-transferable, revocable license to use the App for personal,
              non-commercial purposes in accordance with these Terms.
            </p>

            <h3>Trademarks</h3>
            <p>
              "RunningAI Pro" and associated logos are trademarks of RunningAI Pro. You may not use these trademarks without
              our prior written consent.
            </p>
          </section>

          <section className="terms-section">
            <h2>Third-Party Services</h2>
            <p>RunningAI Pro integrates with third-party services to provide features:</p>

            <div className="third-party-service">
              <h4>OpenAI</h4>
              <p>
                AI coaching features use OpenAI's technology. Your interactions with the AI coach are subject to OpenAI's
                terms and privacy policy. We are not responsible for OpenAI's services or any issues arising from their use.
              </p>
            </div>

            <div className="third-party-service">
              <h4>Weather Services</h4>
              <p>
                Weather data is provided by third-party APIs. We do not guarantee the accuracy or availability of weather information.
              </p>
            </div>

            <div className="third-party-service">
              <h4>Apple Services</h4>
              <p>
                In-app purchases are processed through Apple's App Store, subject to Apple's terms and conditions.
              </p>
            </div>

            <p>
              We are not responsible for the availability, accuracy, or content of third-party services. Use of these
              services is at your own risk.
            </p>
          </section>

          <section className="terms-section">
            <h2>Health and Safety Disclaimer</h2>
            <div className="disclaimer-box">
              <p><strong>IMPORTANT: Please Read Carefully</strong></p>
              <p>
                RunningAI Pro is a fitness tracking and coaching tool, NOT a medical device or professional health service.
                Before beginning any exercise program:
              </p>
              <ul>
                <li>Consult with a physician or qualified healthcare provider</li>
                <li>Ensure you are physically able to engage in running activities</li>
                <li>Inform your doctor of any health conditions or concerns</li>
              </ul>
              <p>
                <strong>Use at Your Own Risk:</strong> Running and physical exercise involve inherent risks including but
                not limited to injury, exhaustion, heart attack, or death. You assume all risks associated with using the App.
              </p>
              <p>
                <strong>Not Medical Advice:</strong> AI coaching, training plans, and race predictions are for informational
                purposes only. They do not constitute medical, professional, or expert advice. Always listen to your body
                and seek professional guidance for health concerns.
              </p>
              <p>
                <strong>GPS Accuracy:</strong> While we strive for accuracy, GPS tracking may have errors. Do not rely
                solely on the App for precise distance or location information.
              </p>
              <p>
                <strong>Outdoor Safety:</strong> When running outdoors, always prioritize safety. Be aware of your
                surroundings, traffic, weather conditions, and other hazards.
              </p>
            </div>
          </section>

          <section className="terms-section">
            <h2>Limitation of Liability</h2>
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, RUNNINGAI AND ITS AFFILIATES, OFFICERS, DIRECTORS, EMPLOYEES, AND
              AGENTS SHALL NOT BE LIABLE FOR:
            </p>
            <ul>
              <li>Any indirect, incidental, special, consequential, or punitive damages</li>
              <li>Loss of profits, revenue, data, or use</li>
              <li>Personal injury or property damage related to your use of the Service</li>
              <li>Any damages arising from third-party services or content</li>
              <li>Errors, interruptions, or unavailability of the Service</li>
              <li>Unauthorized access to your account or data</li>
            </ul>
            <p>
              OUR TOTAL LIABILITY TO YOU FOR ANY CLAIMS ARISING FROM THESE TERMS OR YOUR USE OF THE SERVICE SHALL NOT
              EXCEED THE AMOUNT YOU PAID US IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM, OR $100, WHICHEVER IS GREATER.
            </p>
          </section>

          <section className="terms-section">
            <h2>Disclaimer of Warranties</h2>
            <p>
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED,
              INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.
            </p>
            <p>We do not warrant that:</p>
            <ul>
              <li>The Service will meet your requirements or expectations</li>
              <li>The Service will be uninterrupted, timely, secure, or error-free</li>
              <li>Results or data obtained from the Service will be accurate or reliable</li>
              <li>Any defects or errors will be corrected</li>
            </ul>
          </section>

          <section className="terms-section">
            <h2>Indemnification</h2>
            <p>
              You agree to indemnify, defend, and hold harmless RunningAI Pro and its affiliates, officers, directors, employees,
              and agents from any claims, damages, losses, liabilities, and expenses (including legal fees) arising from:
            </p>
            <ul>
              <li>Your use or misuse of the Service</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of any rights of another party</li>
              <li>Your content or data uploaded to the Service</li>
              <li>Any injuries or damages resulting from your running activities</li>
            </ul>
          </section>

          <section className="terms-section">
            <h2>Modifications to the Service</h2>
            <p>We reserve the right to:</p>
            <ul>
              <li>Modify, suspend, or discontinue any part of the Service at any time</li>
              <li>Change features, functionality, or availability</li>
              <li>Update the App to improve performance or add features</li>
              <li>Remove features or content without notice</li>
            </ul>
            <p>
              We are not liable for any modifications, suspensions, or discontinuations of the Service.
            </p>
          </section>

          <section className="terms-section">
            <h2>Governing Law and Dispute Resolution</h2>

            <h3>Governing Law</h3>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of the United States,
              without regard to conflict of law principles.
            </p>

            <h3>Dispute Resolution</h3>
            <p>
              In the event of any dispute, you agree to first contact us at support@runningaipro.com to attempt to
              resolve the issue informally. Most disputes can be resolved this way.
            </p>

            <h3>Arbitration</h3>
            <p>
              If informal resolution fails, you agree that any dispute shall be resolved through binding arbitration
              in accordance with the rules of the American Arbitration Association. You waive the right to participate
              in class actions or class arbitrations.
            </p>
          </section>

          <section className="terms-section">
            <h2>Apple-Specific Terms</h2>
            <p>As the App is distributed through the Apple App Store, the following additional terms apply:</p>
            <ul>
              <li>These Terms are between you and RunningAI Pro, not Apple</li>
              <li>Apple has no obligation to provide support or maintenance for the App</li>
              <li>Apple is not responsible for any claims related to the App</li>
              <li>You represent that you are not located in a country subject to U.S. embargo</li>
              <li>Apple and its subsidiaries are third-party beneficiaries of these Terms</li>
            </ul>
          </section>

          <section className="terms-section">
            <h2>Severability</h2>
            <p>
              If any provision of these Terms is found to be unenforceable or invalid, that provision will be limited
              or eliminated to the minimum extent necessary so that the remaining Terms remain in full force and effect.
            </p>
          </section>

          <section className="terms-section">
            <h2>Entire Agreement</h2>
            <p>
              These Terms, together with our Privacy Policy, constitute the entire agreement between you and RunningAI Pro
              regarding your use of the Service and supersede all prior agreements and understandings.
            </p>
          </section>

          <section className="terms-section">
            <h2>Contact Information</h2>
            <p>
              If you have questions about these Terms of Service, please contact us:
            </p>
            <div className="contact-info">
              <p><strong>Email:</strong> <a href="mailto:support@runningaipro.com">support@runningaipro.com</a></p>
              <p><strong>Support:</strong> Available through the app's Help section</p>
            </div>
          </section>

          <section className="terms-section acceptance-section">
            <h2>Acceptance of Terms</h2>
            <p>
              BY USING RUNNINGAI, YOU ACKNOWLEDGE THAT YOU HAVE READ, UNDERSTOOD, AND AGREE TO BE BOUND BY THESE TERMS OF
              SERVICE. IF YOU DO NOT AGREE TO THESE TERMS, YOU MUST NOT USE THE APP.
            </p>
          </section>
        </div>
      </div>

      {/* Footer */}
      <footer className="terms-footer">
        <div className="container">
          <p>&copy; 2026 RunningAI Pro. All rights reserved.</p>
          <div className="footer-links">
            <Link to="/">Home</Link>
            <Link to="/privacy">Privacy Policy</Link>
            <Link to="/support">Support</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TermsOfService;
